import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { LogOut, Crown, Calendar, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile({ user, setUser }) {
  const [profile, setProfile] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
    fetchSubscriptionStatus();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        console.error('Error fetching profile:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPlanColor = (plan) => {
    switch(plan) {
      case 'pro': return { bg: '#fef3c7', color: '#b45309', border: '#fcd34d' };
      case 'basic': return { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' };
      default: return { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' };
    }
  };

  return (
    <Layout user={user}>
      <div className="page-header">
        <h1 data-testid="page-title">Profile</h1>
        <p>Manage your account settings</p>
      </div>

      {/* Subscription Card */}
      {subscriptionStatus && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Crown size={20} style={{ color: '#f59e0b' }} />
              Subscription
            </h3>
            <span style={{
              padding: '6px 16px',
              background: getPlanColor(subscriptionStatus.plan).bg,
              color: getPlanColor(subscriptionStatus.plan).color,
              border: `1px solid ${getPlanColor(subscriptionStatus.plan).border}`,
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              {subscriptionStatus.is_active ? subscriptionStatus.plan_name : 'EXPIRED'}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <CreditCard size={16} style={{ color: '#64748b' }} />
                <span style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase' }}>Current Plan</span>
              </div>
              <p style={{ color: '#1e293b', fontSize: '20px', fontWeight: '600', margin: 0 }}>{subscriptionStatus.plan_name}</p>
              <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                {subscriptionStatus.price === 0 ? 'Free forever' : `₹${subscriptionStatus.price}/month`}
              </p>
            </div>

            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Calendar size={16} style={{ color: '#64748b' }} />
                <span style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase' }}>Validity</span>
              </div>
              {subscriptionStatus.plan === 'free' ? (
                <>
                  <p style={{ color: '#1e293b', fontSize: '20px', fontWeight: '600', margin: 0 }}>Unlimited</p>
                  <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>No expiry</p>
                </>
              ) : (
                <>
                  <p style={{ 
                    color: subscriptionStatus.is_active ? 
                      (subscriptionStatus.days_remaining <= 7 ? '#dc2626' : '#22c55e') : '#dc2626', 
                    fontSize: '20px', 
                    fontWeight: '600', 
                    margin: 0 
                  }}>
                    {subscriptionStatus.is_active ? `${subscriptionStatus.days_remaining} days` : 'Expired'}
                  </p>
                  <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                    Expires: {formatDate(subscriptionStatus.subscription_end)}
                  </p>
                </>
              )}
            </div>

            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px' }}>
              <span style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase' }}>Max Sessions</span>
              <p style={{ color: '#1e293b', fontSize: '20px', fontWeight: '600', margin: '8px 0 0 0' }}>{subscriptionStatus.max_sessions}</p>
              <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>WhatsApp accounts</p>
            </div>

            {subscriptionStatus.plan !== 'free' && (
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px' }}>
                <span style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase' }}>Start Date</span>
                <p style={{ color: '#1e293b', fontSize: '16px', fontWeight: '500', margin: '8px 0 0 0' }}>
                  {formatDate(subscriptionStatus.subscription_start)}
                </p>
              </div>
            )}
          </div>

          {/* Features */}
          {subscriptionStatus.features && subscriptionStatus.features.length > 0 && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
              <p style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase', marginBottom: '12px' }}>Plan Features</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {subscriptionStatus.features.map((feature, index) => (
                  <span key={index} style={{
                    padding: '6px 12px',
                    background: '#f0fdf4',
                    color: '#166534',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}>
                    ✓ {feature}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Expiry Warning */}
          {!subscriptionStatus.is_active && subscriptionStatus.plan !== 'free' && (
            <div style={{ 
              marginTop: '20px', 
              padding: '14px 16px', 
              background: '#fee2e2', 
              color: '#991b1b', 
              borderRadius: '10px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              ⚠️ Your subscription has expired. Please contact admin to renew your plan.
            </div>
          )}
          
          {subscriptionStatus.is_active && subscriptionStatus.plan !== 'free' && subscriptionStatus.days_remaining <= 7 && (
            <div style={{ 
              marginTop: '20px', 
              padding: '14px 16px', 
              background: '#fef3c7', 
              color: '#92400e', 
              borderRadius: '10px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              ⚠️ Your subscription expires in {subscriptionStatus.days_remaining} days. Contact admin to extend.
            </div>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>Account Information</h3>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            Loading...
          </div>
        ) : profile ? (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155', fontSize: '14px' }}>
                Email Address
              </label>
              <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', color: '#1e293b' }} data-testid="user-email">
                {profile.email}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155', fontSize: '14px' }}>
                User ID
              </label>
              <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', color: '#1e293b', fontFamily: 'Monaco, monospace', fontSize: '14px' }}>
                {profile.id}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155', fontSize: '14px' }}>
                Account Created
              </label>
              <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', color: '#1e293b' }}>
                {formatDate(profile.created_at)}
              </div>
            </div>

            <button
              className="btn btn-danger"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
            Failed to load profile
          </div>
        )}
      </div>
    </Layout>
  );
}
